import { Box, Button, Card, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { FormEvent, useState } from 'react';
import { Notification, useNotify } from 'react-admin';
import constants from '../../constants';
import { ROLE } from '../../providers/auth.types';
import { getLocalizedErrorMessage } from '../../utils/functions';

const authorizedRoles: ROLE[] = ['admin', 'superadmin', 'business-admin', 'editor', 'group-sessions-viewer', 'expert-manager'];

// Persists the issued admin session exactly like authProvider.login does, then sends the admin
// into the app with a hard redirect so react-admin's checkAuth picks up the fresh token.
const persistSessionAndEnter = (resJson: any): boolean => {
	if (!resJson?.member?.role || authorizedRoles.indexOf(resJson.member.role) === -1) return false;
	localStorage.setItem('token', resJson.token);
	localStorage.setItem('token_exp', (new Date().getTime() + 4 * 60 * 1000).toString());
	localStorage.setItem('refresh_token', resJson.member.refresh_token);
	localStorage.setItem('role', resJson.member.role);
	if (resJson.member.company?._id) localStorage.setItem('company', resJson.member.company._id);
	// Remember-this-device token lets us skip the email OTP on the next login (valid ~7 days).
	if (resJson.member.device_token) localStorage.setItem('admin_device_token', resJson.member.device_token);
	axios.defaults.headers.common['Authorization'] = 'Bearer ' + resJson.token;
	window.location.href = '/';
	return true;
};

const AdminLogin = () => {
	const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [code, setCode] = useState('');
	const [rememberDevice, setRememberDevice] = useState(false);
	const [loading, setLoading] = useState(false);
	const notify = useNotify();

	const handleError = (error: unknown) => {
		if (error instanceof AxiosError) notify(getLocalizedErrorMessage(error, 'he'), { type: 'error' });
		else notify('שגיאה בהתחברות, נסו שוב', { type: 'error' });
	};

	const submitCredentials = async (e: FormEvent) => {
		e.preventDefault();
		setLoading(true);
		try {
			const device_token = localStorage.getItem('admin_device_token') || undefined;
			const { data } = await axios.post(constants.API_URL + 'user/admin', {
				email,
				password,
				platform: 'admin',
				device_token,
			});
			if (data.token) {
				// Trusted device — OTP was skipped, we already have a session.
				if (!persistSessionAndEnter(data)) notify('אין לך הרשאת גישה לאדמין', { type: 'error' });
			} else if (data.otpRequired) {
				setStep('otp');
				notify('קוד אימות נשלח לכתובת המייל שלך', { type: 'info' });
			} else {
				notify('שגיאה בהתחברות', { type: 'error' });
			}
		} catch (error) {
			handleError(error);
		} finally {
			setLoading(false);
		}
	};

	const submitOtp = async (e: FormEvent) => {
		e.preventDefault();
		setLoading(true);
		try {
			const { data } = await axios.post(constants.API_URL + 'user/admin/verify-otp', {
				email,
				code,
				rememberDevice,
			});
			if (!persistSessionAndEnter(data)) notify('אין לך הרשאת גישה לאדמין', { type: 'error' });
		} catch (error) {
			handleError(error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box
			dir="rtl"
			sx={{
				minHeight: '100vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: 'linear-gradient(135deg, #00224B 0%, #0a3a6b 100%)',
				padding: 2,
			}}
		>
			<Card sx={{ width: '100%', maxWidth: 380, padding: 4, textAlign: 'center' }}>
				<Typography variant="h5" sx={{ fontWeight: 600, color: '#00224B', mb: 1 }}>
					GroupHug — אדמין
				</Typography>

				{step === 'credentials' ? (
					<form onSubmit={submitCredentials}>
						<Typography variant="body2" sx={{ color: '#5a6b7b', mb: 3 }}>
							התחברות עם אימייל וסיסמה
						</Typography>
						<TextField
							label="אימייל"
							type="email"
							fullWidth
							required
							autoFocus
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							sx={{ mb: 2 }}
						/>
						<TextField
							label="סיסמה"
							type="password"
							fullWidth
							required
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							sx={{ mb: 3 }}
						/>
						<Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ py: 1.2 }}>
							{loading ? <CircularProgress size={22} color="inherit" /> : 'המשך'}
						</Button>
					</form>
				) : (
					<form onSubmit={submitOtp}>
						<Typography variant="body2" sx={{ color: '#5a6b7b', mb: 3 }}>
							הזינו את קוד האימות שנשלח אל {email}
						</Typography>
						<TextField
							label="קוד אימות"
							type="text"
							fullWidth
							required
							autoFocus
							value={code}
							onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
							inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: 6, textAlign: 'center' } }}
							sx={{ mb: 2 }}
						/>
						<FormControlLabel
							control={<Checkbox checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />}
							label="זכור את המכשיר הזה (7 ימים)"
							sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, ml: 0 }}
						/>
						<Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ py: 1.2, mb: 1 }}>
							{loading ? <CircularProgress size={22} color="inherit" /> : 'התחברות'}
						</Button>
						<Button
							type="button"
							variant="text"
							fullWidth
							disabled={loading}
							onClick={() => {
								setStep('credentials');
								setCode('');
							}}
						>
							חזרה
						</Button>
					</form>
				)}
			</Card>
			<Notification />
		</Box>
	);
};

export default AdminLogin;
